import {
  Box,
  Button,
  Callout,
  Flex,
  Heading,
  IconButton,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { Outlet } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon, ReloadIcon } from "@radix-ui/react-icons";
import {
  useAvailableStores,
  type AvailableStoresOptions,
} from "../../../state/available-stores";
import { useUserPosition } from "../../../hooks/useUserPosition";
import { StoreCard } from "./StoreCard";

/**
 * Factorized proofing list component.
 * Renders loading skeletons, empty state, and store cards.
 * Callers provide the heading / surrounding chrome.
 */
export interface ProofingListProps {
  userId?: string;
  position?: { latitude: number; longitude: number };
  viewAsMode?: boolean;
}

export function ProofingList({
  userId,
  position,
  viewAsMode,
}: ProofingListProps) {
  const { t } = useTranslation();

  const options: AvailableStoresOptions | undefined =
    userId && position ? { userId, position } : undefined;
  const { stores, loading } = useAvailableStores(options);

  return (
    <>
      {loading && (
        <>
          <Skeleton width="100%" height="60px" />
          <Skeleton width="100%" height="60px" />
          <Skeleton width="100%" height="60px" />
        </>
      )}

      {!loading && stores.length === 0 && (
        <Text color="gray">{t("proofing.noStores")}</Text>
      )}

      {!loading &&
        stores.map((store) => (
          <StoreCard
            store={store}
            key={store.store_id}
            viewAsMode={viewAsMode}
            userId={userId}
          />
        ))}
    </>
  );
}

export function Proofing() {
  const { t } = useTranslation();
  const { loading } = useAvailableStores();
  const { refreshPosition, positionError } = useUserPosition();
  const geolocationDenied = positionError != null;

  // Re-fetch position when user comes back to the app (e.g. unlocks phone after driving)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshPosition();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshPosition]);

  return (
    <>
      {/* Main list content */}
      <Box
        style={{
          height: "100%",
          overflowY: "auto",
        }}
      >
        <Flex p="4" gap="2" direction="column">
          <Flex justify="between" align="center">
            <Heading size="4">{t("proofing.title")}</Heading>
            <IconButton
              size="2"
              variant="ghost"
              onClick={refreshPosition}
              disabled={loading && !geolocationDenied}
            >
              <ReloadIcon />
            </IconButton>
          </Flex>

          {geolocationDenied && (
            <Callout.Root color="amber">
              <Callout.Icon>
                <ExclamationTriangleIcon />
              </Callout.Icon>
              <Flex direction="column" gap="2" style={{ flex: 1 }}>
                <Text size="2">
                  {positionError === "denied"
                    ? t("proofing.geolocationDenied.messageDenied")
                    : t("proofing.geolocationDenied.messageUnavailable")}
                </Text>
                {positionError === "denied" && (
                  <Text size="1" color="gray">
                    {t("proofing.geolocationDenied.instructions")}
                  </Text>
                )}
                <Button
                  size="1"
                  variant="soft"
                  style={{ alignSelf: "flex-end" }}
                  onClick={
                    positionError === "denied"
                      ? () => window.location.reload()
                      : refreshPosition
                  }
                >
                  {positionError === "denied"
                    ? t("proofing.geolocationDenied.reloadPage")
                    : t("proofing.geolocationDenied.tryAgain")}
                </Button>
              </Flex>
            </Callout.Root>
          )}

          {!geolocationDenied && <ProofingList />}
        </Flex>
      </Box>

      {/* Capture overlay renders here via nested route */}
      <Outlet />
    </>
  );
}
